'use client';

import { useState, useRef, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import type { Locale } from '../HomeLanding';

const REQUEST_TYPES = [
  { value: '', label: 'Select request type' },
  { value: 'product-specs', label: 'Product Specifications' },
  { value: 'coa-samples', label: 'COA Samples' },
  { value: 'export-docs', label: 'Export Documentation' },
  { value: 'sustainability', label: 'Sustainability Summary' },
];

const INCLUDED = [
  'Product specification sheet',
  'Certificate of Analysis sample',
  'Fatty acid profile overview',
  'Export and compliance notes',
  'Sourcing and sustainability summary',
  'Quotation request guidance',
];

const CONTACT_ENDPOINTS = ['/api/send-contact'];

export default function ProtocolLibraryCTA({ locale = 'en' }: { locale?: Locale }) {
  const isVietnamese = locale === 'vn';
  const isIndonesian = locale === 'id';
  const isChinese = locale === 'cn';
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [requestType, setRequestType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaA, setCaptchaA] = useState(3);
  const [captchaB, setCaptchaB] = useState(4);
  const [captchaC, setCaptchaC] = useState(5);
  const [captchaInput, setCaptchaInput] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);

  const requestTypes = isVietnamese
    ? [
        { value: '', label: 'Chọn loại yêu cầu' },
        { value: 'product-specs', label: 'Thông số sản phẩm' },
        { value: 'coa-samples', label: 'Mẫu COA' },
        { value: 'export-docs', label: 'Tài liệu xuất khẩu' },
        { value: 'sustainability', label: 'Tóm tắt bền vững' },
      ]
    : isIndonesian
      ? [
          { value: '', label: 'Pilih jenis permintaan' },
          { value: 'product-specs', label: 'Spesifikasi produk' },
          { value: 'coa-samples', label: 'Contoh COA' },
          { value: 'export-docs', label: 'Dokumen ekspor' },
          { value: 'sustainability', label: 'Ringkasan keberlanjutan' },
        ]
      : isChinese
      ? [
        { value: '', label: '请选择需求类型' },
        { value: 'product-specs', label: '产品规格' },
        { value: 'coa-samples', label: 'COA 样本' },
        { value: 'export-docs', label: '出口文件' },
        { value: 'sustainability', label: '可持续性摘要' },
      ]
        : REQUEST_TYPES;

  const included = isVietnamese
    ? [
        'Bảng thông số sản phẩm',
        'Mẫu Certificate of Analysis',
        'Tổng quan hồ sơ axit béo',
        'Ghi chú xuất khẩu và tuân thủ',
        'Tóm tắt nguồn cung bền vững',
        'Hướng dẫn yêu cầu báo giá',
      ]
    : isIndonesian
      ? [
          'Lembar spesifikasi produk',
          'Contoh Certificate of Analysis',
          'Gambaran profil asam lemak',
          'Catatan ekspor dan kepatuhan',
          'Ringkasan sumber dan keberlanjutan',
          'Panduan permintaan penawaran',
        ]
    : isChinese
      ? [
          '产品规格表',
          'Certificate of Analysis 样本',
          '脂肪酸概况',
          '出口与合规说明',
          '可持续采购摘要',
          '报价请求指南',
        ]
    : INCLUDED;
  const generateCaptcha = () => {
    const nextA = Math.floor(Math.random() * 8) + 2;
    const nextB = Math.floor(Math.random() * 8) + 1;
    const nextC = Math.floor(Math.random() * 8) + 1;

    setCaptchaA(nextA);
    setCaptchaB(nextB);
    setCaptchaC(nextC);
    setCaptchaInput('');
  };

  const postContactForm = async (formData: URLSearchParams) => {
    let lastError: string | null = null;

    for (const endpoint of CONTACT_ENDPOINTS) {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        const data = await res.json().catch(() => null);

        if (res.ok && data?.success) {
          return {
            ok: true,
            message: data?.message || '',
            notifications: data?.notifications ?? null,
            endpoint,
          };
        }

        lastError = data?.message || data?.error || `Request failed at ${endpoint}`;
      } catch (error) {
        lastError = error instanceof Error ? error.message : `Request failed at ${endpoint}`;
      }
    }

    return {
      ok: false,
      message: lastError || 'Failed to send request. Please try again later.',
      notifications: null,
      endpoint: null,
    };
  };

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.pl-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), i * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName || !email || !company || !phone || !requestType) {
      setError(
        isVietnamese
          ? 'Vui lòng điền đầy đủ các trường để yêu cầu bộ tài liệu.'
          : isIndonesian
            ? 'Lengkapi semua kolom untuk meminta paket produk.'
          : isChinese
            ? '请完整填写所有字段后再提交。'
            : 'Please complete all fields to request the product pack.'
      );
      return;
    }

    if (Number(captchaInput) !== captchaA + captchaB + captchaC) {
      setError(
        isVietnamese
          ? 'Vui lòng giải captcha toán trước khi gửi.'
          : isIndonesian
            ? 'Selesaikan captcha matematika sebelum mengirim.'
          : isChinese
            ? '请先完成数学验证码。'
            : 'Please solve the math captcha before submitting.'
      );
      generateCaptcha();
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.set('full_name', fullName);
      formData.set('company_name', company);
      formData.set('email', email);
      formData.set('phone_number', phone);
      formData.set('request_type', requestType);
      formData.set('spam_answer', captchaInput.trim());
      formData.set('spam_a', String(captchaA));
      formData.set('spam_b', String(captchaB));
      formData.set('spam_c', String(captchaC));

      const result = await postContactForm(formData);

      if (result.ok) {
        setSubmitted(true);
        setCaptchaInput('');
        generateCaptcha();
      } else {
        setError(result.message);
        generateCaptcha();
      }
    } catch {
      setError('Failed to send request. Please try again later.');
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 bg-card-bg scroll-mt-20 relative overflow-hidden noise-overlay"
      aria-label={isVietnamese ? 'Yêu cầu bộ tài liệu sản phẩm' : isIndonesian ? 'Permintaan paket produk' : isChinese ? '产品资料申请' : 'Product pack request'}
    >
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,119,182,0.08) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,119,182,0.05) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center gap-3 mb-6 pl-reveal reveal">
              <span className="inline-block w-6 h-px bg-blue" />
              <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
                {isVietnamese
                  ? 'Bộ tài liệu sản phẩm - Phiên bản 2026'
                  : isIndonesian
                    ? 'Paket produk - Edisi 2026'
                  : isChinese
                    ? '产品资料包 - 2026 版'
                    : 'Product Pack - 2026 Edition'}
              </span>
            </div>
            <h2
              className="pl-reveal reveal reveal-delay-1 font-bold tracking-[-0.04em] text-white leading-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              {isVietnamese
                ? 'Yêu cầu bộ tài liệu sản phẩm.'
                : isIndonesian
                  ? 'Minta paket produk.'
                : isChinese
                  ? '申请产品资料包'
                  : 'Request the product pack.'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese
                  ? 'Thông số, COA và tuân thủ trong một nơi.'
                  : isIndonesian
                    ? 'Spesifikasi, COA, dan kepatuhan dalam satu tempat.'
                  : isChinese
                    ? '规格、COA 与合规内容集中在一处。'
                    : 'Specs, COA, and compliance in one place.'}
              </span>
            </h2>
            <p className="pl-reveal reveal reveal-delay-2 text-titanium text-[15px] leading-relaxed mb-10 max-w-md">
              {isVietnamese
                ? 'Gửi yêu cầu của bạn và chúng tôi sẽ chia sẻ bảng thông số sản phẩm, chứng từ lô hàng và tài liệu sẵn sàng xuất khẩu để bộ phận mua hàng xem xét.'
                : isIndonesian
                  ? 'Kirim permintaan Anda dan kami akan membagikan lembar produk, dokumentasi batch, serta referensi siap ekspor untuk ditinjau tim pembelian.'
                : isChinese
                  ? '提交您的请求后，我们将分享产品规格表、批次文件和可直接用于出口审核的资料。'
                  : 'Send your request and we will share the product sheet, batch documentation, and export-ready references for procurement review.'}
            </p>

            <div className="pl-reveal reveal reveal-delay-3 space-y-0">
              <div className="font-mono text-[10px] tracking-[0.15em] text-titanium uppercase mb-4">
                {isVietnamese ? 'Bao gồm trong bộ tài liệu' : isIndonesian ? 'Isi paket' : isChinese ? '资料包含' : 'Included in the pack'}
              </div>
              {included.map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-3 border-b border-card-border last:border-0">
                  <span className="font-mono text-[11px] text-blue shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[13px] text-titanium leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pl-reveal reveal reveal-delay-2">
            {!submitted ? (
              <div className="space-y-4">
                <div className="bg-surface border border-divider rounded-md p-8">
                  <div className="flex items-center gap-2.5 mb-6">
                    <AppLogo
                      src="/assets/images/logo_small.png"
                      text="Indonesia Fish Oil"
                      size={18}
                      className="text-charcoal font-semibold tracking-[0.12em] text-[12px] uppercase"
                      textClassName="text-[12px] font-semibold tracking-[0.12em] text-charcoal uppercase"
                    />
                  </div>

                  <div className="mb-6">
                    <h3 className="font-semibold text-charcoal text-xl tracking-tight mb-1">
                      {isVietnamese
                        ? 'Yêu cầu thông số sản phẩm / COA'
                        : isIndonesian
                          ? 'Minta spesifikasi produk / COA'
                        : isChinese
                          ? '产品规格 / COA 请求'
                          : 'Request Product Specs / COA'}
                    </h3>
                    <p className="text-titanium text-[13px]">
                      {isVietnamese
                        ? 'Truy cập nhanh cho người mua, người phối trộn và đội mua hàng.'
                        : isIndonesian
                          ? 'Akses cepat untuk pembeli, formulator, dan tim pembelian.'
                        : isChinese
                          ? '供采购、配方与销售团队快速获取资料。'
                          : 'Quick access for buyers, formulators, and procurement teams.'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="pl-full-name" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                          {isVietnamese ? 'Họ và tên' : isIndonesian ? 'Nama lengkap' : isChinese ? '姓名' : 'Full Name'}
                        </label>
                        <input
                          id="pl-full-name"
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder={isVietnamese ? 'Họ tên của bạn' : isIndonesian ? 'Nama lengkap Anda' : isChinese ? '请输入姓名' : 'Your full name'}
                          className="w-full px-4 py-3 bg-canvas border border-divider rounded text-[14px] text-charcoal placeholder-titanium focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="pl-company" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                          {isVietnamese ? 'Công ty' : isIndonesian ? 'Perusahaan' : isChinese ? '公司' : 'Company'}
                        </label>
                        <input
                          id="pl-company"
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          placeholder={isVietnamese ? 'Tên công ty hoặc nhà máy thức ăn' : isIndonesian ? 'Nama perusahaan atau pabrik pakan' : isChinese ? '公司或饲料厂名称' : 'Company or feed mill name'}
                          className="w-full px-4 py-3 bg-canvas border border-divider rounded text-[14px] text-charcoal placeholder-titanium focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="pl-email" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                        {isVietnamese ? 'Email' : 'Email'}
                        </label>
                        <input
                          id="pl-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full px-4 py-3 bg-canvas border border-divider rounded text-[14px] text-charcoal placeholder-titanium focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="pl-phone" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                          {isVietnamese ? 'Điện thoại/WhatsApp' : isIndonesian ? 'Ponsel/WhatsApp' : isChinese ? '手机 / WhatsApp' : 'Mobile Phone/Whatsapp'}
                        </label>
                        <input
                          id="pl-phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder={isVietnamese ? '+84 9xx xxxx xxx' : isIndonesian ? '+62 8xx xxxx xxxx' : isChinese ? '+86 1xx xxxx xxxx' : '+62 8xx xxxx xxxx'}
                          className="w-full px-4 py-3 bg-canvas border border-divider rounded text-[14px] text-charcoal placeholder-titanium focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="pl-request" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                        {isVietnamese ? 'Loại yêu cầu' : isIndonesian ? 'Jenis permintaan' : isChinese ? '需求类型' : 'Request Type'}
                      </label>
                      <select
                        id="pl-request"
                        value={requestType}
                        onChange={(e) => setRequestType(e.target.value)}
                        className="select-field w-full px-4 py-3 bg-canvas border border-divider rounded text-[14px] text-charcoal focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                        required
                      >
                        {requestTypes.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="pl-captcha" className="block font-mono text-[11px] tracking-[0.12em] text-titanium uppercase mb-1.5">
                        {isVietnamese ? 'Xác minh bảo mật' : isIndonesian ? 'Verifikasi keamanan' : isChinese ? '安全验证' : 'Security Check'}
                      </label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded border border-divider bg-canvas px-4 py-3">
                        <div className="text-[14px] text-charcoal">
                          {isVietnamese ? 'Kết quả của' : isIndonesian ? 'Berapakah hasil' : isChinese ? '请计算' : 'What is'}{' '}
                          <span className="font-semibold">{captchaA}</span> +{' '}
                          <span className="font-semibold">{captchaB}</span> +{' '}
                          <span className="font-semibold">{captchaC}</span>?
                        </div>
                        <div className="flex items-center gap-2 sm:ml-auto">
                          <input
                            id="pl-captcha"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={captchaInput}
                            onChange={(e) => setCaptchaInput(e.target.value)}
                            placeholder={isVietnamese ? 'Đáp án' : isIndonesian ? 'Jawaban' : isChinese ? '答案' : 'Answer'}
                            className="w-24 px-3 py-2 bg-white border border-divider rounded text-[14px] text-charcoal placeholder-titanium focus:outline-none focus:border-blue focus:shadow-blue-glow transition-all duration-200"
                            required
                          />
                          <button
                            type="button"
                            onClick={generateCaptcha}
                            className="inline-flex items-center justify-center rounded border border-divider bg-white px-3 py-2 text-[12px] font-medium text-titanium hover:text-charcoal hover:border-blue transition-colors duration-200"
                            aria-label={isVietnamese ? 'Làm mới captcha' : isIndonesian ? 'Muat ulang captcha' : isChinese ? '刷新验证码' : 'Refresh captcha'}
                          >
                            {isVietnamese ? 'Làm mới' : isIndonesian ? 'Muat ulang' : isChinese ? '刷新' : 'Refresh'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {error && <p className="text-[12px] text-red-600 font-mono">{error}</p>}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-between px-6 py-4 bg-blue text-white rounded font-semibold text-[14px] hover:bg-blue-dim transition-colors duration-200 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span>
                        {isSubmitting
                          ? isVietnamese
                            ? 'Mengirim...'
                            : isIndonesian
                              ? 'Mengirim...'
                              : isChinese
                                ? '发送中...'
                                : 'Sending...'
                          : isVietnamese
                            ? 'Yêu cầu bộ tài liệu'
                            : isIndonesian
                              ? 'Minta paket produk'
                              : isChinese
                                ? '申请资料包'
                                : 'Request Product Pack'}
                      </span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2v8M4 7l4 4 4-4M2 13h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </form>

                  <p className="mt-4 text-[11px] text-titanium font-mono text-center">
                    {isVietnamese
                      ? 'Không spam. Chúng tôi chỉ gửi tài liệu bạn yêu cầu.'
                      : isIndonesian
                        ? 'Tanpa spam. Kami hanya mengirim dokumen yang Anda minta.'
                      : isChinese
                        ? '不会群发，仅发送您请求的资料。'
                        : 'No spam. We only send the documents you requested.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-surface border border-divider rounded-md p-8 flex flex-col items-center text-center gap-5">
                <div className="w-14 h-14 rounded-full bg-metric-green/10 flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0A7A4E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal text-xl mb-2">
                    {isVietnamese ? 'Đã nhận yêu cầu.' : isIndonesian ? 'Permintaan diterima.' : isChinese ? '已收到请求。' : 'Request received.'}
                  </h3>
                  <p className="text-titanium text-[14px] leading-relaxed">
                    {isVietnamese
                      ? 'Chúng tôi sẽ gửi bộ tài liệu, mẫu COA và tài liệu bạn yêu cầu vào hộp thư của bạn trong thời gian sớm nhất.'
                      : isIndonesian
                        ? 'Kami akan segera mengirim paket produk, contoh COA, dan dokumen yang diminta ke inbox Anda.'
                        : isChinese
                        ? '我们会尽快将资料包、COA 样本和所需文件发送到您的邮箱。'
                        : 'We will send the product pack, COA sample, and requested documentation to your inbox shortly.'}
                  </p>
                </div>
                <div className="w-full pt-4 border-t border-divider">
                  <p className="text-[12px] font-mono text-titanium">
                    {isVietnamese
                      ? 'Nếu cần, chúng tôi cũng có thể chuẩn bị báo giá cho kế hoạch xuất khẩu.'
                      : isIndonesian
                        ? 'Jika perlu, kami juga dapat menyiapkan penawaran untuk perencanaan ekspor.'
                        : isChinese
                        ? '如有需要，我们也可为出口计划准备报价。'
                        : 'If needed, we can also prepare a quote for export planning.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

